/*
The complete list of all account analytics events in rivet
See RVT-14660 for rationale

Design considerations:
  * Make it hard to log an event that isn't explicitly enumerated here
  * Must not be directly callable from ng template expressions (to avoid loose type checking)
*/

import { AssignmentType } from '@rivethealth/data/denials';
import {
  ProcedureCoverage,
  TableEstimationStatus,
} from '@rivethealth/data/estimation';
import { PaymentTransactionPostStatus } from '@rivethealth/data/payment-transaction';
import { Language } from '@rivethealth/data/translation';
import { ValueFilterNode } from '../advanced-rule-filter';
import {
  SentenceCase,
  SentenceCaseFromCamelCase,
  SentenceCaseFromKebabCase,
  SentenceCaseFromSnakeCase,
} from '../capitalization';
import { CalculatedConsentStatus } from '../consent';
import { ClaimProperty } from '../denial-assignment';
import { EligibilityStatus } from '../eligibility';
import { NonResponseProperty } from '../non-response';
import { AppointmentFilters, PatientInsuranceIssue } from '../patient';
import { PPA_AnalyticsEvents } from '../payer-portal-automator';
import { KeyMetricsGrouping } from '../revenue-diagnostics';
import { friendlyMultiSelectOperator } from '../super-table';
import { EventType } from './event-types';

export const accountEventTypes: AccountEventType<any>[] = [];
export class AccountEventType<T = undefined> extends EventType<T> {
  constructor(
    readonly name: string,
    readonly description: string,
  ) {
    super(name, description);
    accountEventTypes.push(this);
  }
}

// ************************************************************
// Types used in event definitions

export interface AdvancedRuleAnalyticProperties {
  'grouping selected': 'All' | 'Any';
  'num rules applied': number;
  'num total': number;
  'num assignees': number;
}
export interface ComposerAnalyticProperties {
  method: 'Email' | 'SMS';
  'phi status': SentenceCase<CalculatedConsentStatus> | '(Mixed)' | null;
  'num distinct recipients': number;
  'sending to same patient multiple ways': boolean;
}
export interface DenialsAutomationAnalyticalProperties {
  'grouping selected': 'All' | 'Any';
  'first rule filter': SentenceCaseFromSnakeCase<ClaimProperty>;
  'first rule operator': SentenceCaseFromSnakeCase<
    Exclude<ValueFilterNode<ClaimProperty>['value'], null>['operator']
  >;
  'num rules in automation': number;
  'total denials automations after': number;
}
export interface DenialsInstructionAnalyticProperties {
  'has note': boolean;
  'has attachment': boolean;
  'num contacts': number;
  'num policies': number;
  'num forms': number;
  'total instructions on account after': number;
}
export interface ExclusionRuleAnalyticProperties {
  'grouping selected': 'All' | 'Any';
  'num rules applied': number;
  'num total': number;
}
export interface IssueStatusesAnalyticProperties {
  'num statuses added': number;
  'total statuses after': number;
}
export type ModalCloseMethod =
  | 'X out'
  | 'Press escape key'
  | `Click ${string}`
  | 'Upload file'
  | 'Download file'
  | 'Replace with template'
  | 'Complete estimate'
  | 'Unknown';
export interface NonresponseAutomationAnalyticalProperties {
  'grouping selected': 'All' | 'Any';
  'first rule filter': SentenceCaseFromSnakeCase<NonResponseProperty>;
  'first rule operator': SentenceCaseFromSnakeCase<
    Exclude<ValueFilterNode<NonResponseProperty>['value'], null>['operator']
  >;
  'num rules in automation': number;
  'total non-response automations after': number;
}
export interface NoteAnalyticProperties {
  'is bulk action': boolean;
  'num items affected': number | 'Unknown (bulk action)';
  'num attachments': number | 'N/A';
  'days from reminder date': number | 'N/A';
}
export interface PayerContactAnalyticProperties {
  'applies to all denials': boolean;
  'total payer contacts on account after': number;
}
export interface PayerFormTemplateAnalyticProperties {
  'applies to all denials': boolean;
  'total payer form templates on account after': number;
}
export interface PayerPolicyAnalyticProperties {
  'applies to all denials': boolean;
  'total payer policies on account after': number;
}
export interface ProcessPaymentAnalyticProperties {
  'did succeed': boolean;
  'did save card': boolean;
  'is full payment': boolean;
  'is subsequent payment': boolean;
  amount: number;
  'error message': string | null;
  'submitted by': 'Patient' | 'Office staff';
}
export interface SavedViewAnalyticalProperties {
  'saved view name': string | null;
  'is shared': boolean;
  'total private views after': number;
  'total public views after': number;
  'total shared views after': number;
}
export interface SendEstimateAnalyticProperties {
  'payment requested': boolean | 'Unknown (bulk send)';
  'is bulk action': boolean;
  'num estimates selected': number;
}

// ************************************************************
// List of events, alphabetically
// Add name + properties for new analytics events
// If possible, use one of the existing properties types (above)
// Remember to update the documentation string

export const AddComment = new AccountEventType<NoteAnalyticProperties>(
  'Add comment',
  `
  * Claims > *any claim* > *write a comment* > Save
  * Denials > Worklist > *any claim* > *write a comment* > *optionally upload attachment(s)* > Save
  * Denials > Worklist > *any claim* > *write a comment* > *optionally schedule reminder* > Submit
  * Denials > Worklist > *select denial checkboxes* > Add comment > *write a comment* > *optionally schedule reminder* > Submit
  * Non-response > *any claim* > *write a comment* > Save
  * Underpayments > *any claim* > *write a comment* > Save
  * Estimates > *any estimate* > Activity timeline > *write a comment* > Add
  * Estimates > Create estimate > *write a comment* > Create
  `,
);
export const AddCodeBundle = new AccountEventType<{
  source: 'estimate builder' | 'estimate template';
}>(
  'Add code bundle',
  `
  * Estimates > *any estimate* > *any treatment* > Code Bundles > *click on a code bundle*
  * Estimates > *any estimate* > Save As Template > Code Bundles > *click on a code bundle*
  * Settings > Estimates > Estimate Templates > *any template* > *any treatment* > Code Bundles > *click on a code bundle*
  `,
);
export const AddComparisonsToWorkbook = new AccountEventType<{
  'num comparisons': number;
  'num fee schedule comparisons': number;
  'num baseline comparisons': number;
}>(
  'Add comparison(s) to workbook',
  `
  * Modeler > *any workbook* > Actions > New comparison > *follow steps* > Complete
  `,
);
export const AddDocumentComment = new AccountEventType<{ isRiveter: boolean }>(
  'Add document comment',
  `
  * Payer documents > *any document row* > three dot menu > click on View Comments > type comment > click save
  `,
);

export const ApplyFilter = new AccountEventType<{
  // Filter names are usually typed, but some of the type definitions are inside
  // client/ files which we can't import from here. Make sure to use
  // sentenceCaseFromCamelCase(filterName) or the appropriate equivalent.
  'filter name': string;
  // May be inaccurate operator name for dates and numbers, but we don't have
  // a clear way to tell if a filter is a multi-select, date, or number.
  'filter operator selected':
    | ReturnType<typeof friendlyMultiSelectOperator>
    | '$'
    | '%'
    | null;
  // https://github.com/rivethealth/rivet/pull/6893#discussion_r1699070621
  'filter value': string | `${any}`;
  'num options selected': number | 'N/A';
  'from saved view': boolean | '(Saved views not supported)';
  'total filters active after': number;
  'filters active after': string[];
}>(
  'Apply filter',
  `
  * Communication > *apply any filter*
  * Appointments > *apply any filter*
  * Eligibility > *apply any filter*
  * Estimates > *apply any filter*
  * Patients > *any patient* > Communication > *apply any filter*
  * Patients > *any patient* > Eligibility > *apply any filter*
  * Patients > *any patient* > Estimates > *apply any filter*
  * Payments > *apply any filter*
  * All rates > *apply any filter*
  * Claims > *apply any filter*
  * Denials > Worklist > *apply any filter*
  * Denials > Analytics > *apply any filter*
  * Fee schedules > *apply any filter*
  * Non-response > *apply any filter*
  * Payers > *apply any filter*
  * Payers > *any payer* > Communication > *apply any filter*
  * Payers > *any payer* > Fee schedules > *apply any filter*
  * Payers > *any payer* > Schedule mapping > *apply any filter*
  * Payers > *any payer* > Contracts > *apply any filter*
  * Payers > *any payer* > Contacts > *apply any filter*
  * Payers > *any payer* > Files > *apply any filter*
  * Pricing optimization > *apply any filter*
  * Underpayments > *apply any filter*
  * Claims analytics > *apply any filter*
  * Contract terms > *apply any filter*
  * Estimate accuracy > *apply any filter*
  * Estimates analytics > *apply any filter*
  * SuperScore > *apply any filter*
  * Procedure comparison > *apply any filter*
  * Settings > Users > *apply any filter*
  * Settings > Payer documents > *apply any filter*
  * Settings > Denials > Instructions > *apply any filter*
  * Settings > Insurance product crosswalk > *apply any filter*
  * Reports > *apply any filter*
  `,
);
// The batch eligibility modal does not check the results after submission, so
// some of these properties are 'Unknown' until we build a results view.
// Additionally, this event is not implemented on the EZ estimator yet.
export const CheckEligibility = new AccountEventType<{
  'is successful': boolean | 'Unknown (batch submission)';
  'is automated': boolean;
  'is bulk action': boolean;
  'num patients': number;
  'num accumulative checks': number | 'Unknown (batch submission)';
  'error message': string | null | 'Unknown (batch submission)';
  status: SentenceCase<EligibilityStatus> | 'Unknown (batch submission)';
  'num service types': number;
  'request origin': 'backend' | 'frontend';
}>(
  'Check eligibility',
  `
  * Appointments > *checkmark appointments* > Check eligibility
  * Eligibility > Check eligibility > *fill out modal* > Submit
  * Patients > *checkmark patients* > Check eligibility
  `,
);

export const ClickApgGetAllowedAmount = new AccountEventType(
  'Click apg get allowed amount button',
  `
  * Estimates > *any estimate* > *select apg fee schedule* > Get Allowed Amount
  `,
);
export const ClickBookmarkEditButton = new AccountEventType(
  'Click "Edit bookmarks" button on Welcome screen',
  `
  *Welcome page > *Edit bookmarks button*
  `,
);
export const ClickBookmarkSaveButton = new AccountEventType<{
  selectedBookmarks: string[];
  numSelectedBookmarks: number;
}>(
  'Click "Save" button on Bookmarks modal',
  `
  *Welcome page > click *Edit bookmarks* > click *Save*
  `,
);
export const ClickBookmarkTile = new AccountEventType<{
  title: string;
}>(
  'Click bookmark tile on Welcome screen',
  `
  * Welcome screen > click bookmark tile
  `,
);
export const ClickCreateProjectButton = new AccountEventType(
  'Click create project button from work-list',
  `
  *Underpayments > *select underpayments* > *create project button*
  `,
);
export const ClickLearnAboutModelerButton = new AccountEventType(
  'Click learn about modeler button',
  `
  * *modeler homepage with no workbooks* > Learn about Modeler
  `,
);
export const ClickProjectsNotifications = new AccountEventType(
  'Click projects notification bell',
  `
  * Projects > *notification bell*
  `,
);
export const ClickNavigationMenuItem = new AccountEventType<{
  'top level': boolean;
  title: string;
  'ancestor titles': string | null;
  locked: boolean;
}>(
  'Click navigation menu item',
  `
  * Main navigation bar > click item or sub-item
  `,
);
export const ClickViewProjectsDashboardButton = new AccountEventType(
  'Click view projects dashboard button from work-list',
  `
  *Underpayments > *view project dashboard*
  `,
);
export const ClickViewWorklistButton = new AccountEventType(
  'Click view work-list button from projects dashboard',
  `
  *Projects > *view worklist*
  `,
);
export const ClickSavedView = new AccountEventType<{
  'is shared': boolean;
  'saved view name': string;
}>(
  'Click saved view',
  `
  * *table that supports saved views* > *click to apply a saved view*
  `,
);
export const ClickSearchBar = new AccountEventType<{
  'table name': string | null;
}>(
  'Click search bar',
  `
  * *table that has a search bar* > *click to focus the search bar*
  `,
);
export const CloneFeeSchedule = new AccountEventType<{
  'cloned as': 'Fee schedule' | 'Baseline schedule';
}>(
  'Clone fee schedule',
  `
  * Fee Schedules > *any fee schedule* ⋯ > Clone
  * Payers > *any payer* > Fee schedules > *any fee schedule* ⋯ > Clone
  * Payers > *any payer* > Fee schedules > *open any fee schedule* > Actions > Clone schedule / Clone schedule to baseline
  `,
);
export const CloseModal = new AccountEventType<{
  'modal title': string;
  'close method': ModalCloseMethod;
}>(
  'Close modal',
  `
  * *any modal* > *press the X to close*
  * *any modal* > *press Escape key*
  * *any modal with a close button* > *click Close at the bottom*
  * *any modal with a cancel button* > *click Cancel at the bottom*
  * *any modal with a submit button* > *click Submit at the bottom*
  * *any modal with a delete button* > *click Delete at the bottom*
  `,
);
export const CollapseComparison = new AccountEventType<{
  'is reference': boolean;
}>(
  'Collapse comparison',
  `
  * Modeler > *any workbook* > *triple dot menu for any comparison* > Collapse
  `,
);
export const CompleteEstimate = new AccountEventType<{
  'num treatments': number;
  'num payers': number;
  'num codes': number;
  'num modifiers': number;
  'num payers out of network': number;
  'num inpatient treatments': number;
  'num outpatient treatments': number;
  'has payment request': boolean;
  'has signature line': boolean;
  'has note': boolean;
  'has service location contact info': boolean;
  'has address information': boolean;
  'has diagnosis': boolean;
  'has apg codes': boolean;
  'has assignee': boolean;
  'has estimate due date': boolean;
  'has payment due date': boolean;
  'has appointment linked': boolean;
  'has custom service types': boolean | '(Mixed)';
  'has custom charged amounts': boolean;
  'has custom allowed amounts': boolean;
  'has custom coinsurance': boolean;
  'has custom copay': boolean;
  'has custom deductible': boolean;
  'has custom oop': boolean;
  'has alternate coinsurance': boolean;
  'has alternate copay': boolean;
  'has alternate deductible': boolean;
  'has alternate oop': boolean;
  'has alternate limitation': boolean;
  'has self pay payer': boolean;
  'is short form': boolean;
  'pdf language': SentenceCase<Language>;
  'procedure coverage':
    | SentenceCaseFromKebabCase<ProcedureCoverage>
    | '(Mixed)';
  'discount type': '$' | '%' | '(Mixed)' | null;
  'treatment type':
    | 'Professional'
    | 'Facility (ASC)'
    | 'Inpatient Hospital'
    | 'Outpatient Hospital'
    | '(Mixed)';
  'total allowed': number;
  'total patient responsibility': number;
}>(
  'Complete estimate',
  `
  * Estimates > *any incomplete estimate* > Complete
  * Patients > *any patient* > Estimates > *any incomplete estimate* > Complete
  `,
);
export const CopyToClipboard = new AccountEventType<{
  'time period': 'All' | 'Today';
}>(
  'Copy to clipboard',
  `
  * Claims > *any claim* > Activity ⋯ > Copy all / Copy today
  * Denials > Worklist > *any claim* > Activity ⋯ > Copy all / Copy today
  * Non-response > *any claim* > Activity ⋯ > Copy all / Copy today
  * Underpayments > *any claim* > Activity ⋯ > Copy all / Copy today
  `,
);
export const CreateAutoEstimateRule = new AccountEventType(
  'Create auto estimate rule',
  `
  * Settings > Estimates > Automation > Location mapping > Create rule > *fill out modal* > Create rule
  * Settings > Estimates > Automation > Location mapping > *any rule* > ⋯ > Clone > Create rule
  `,
);
export const CreateCall = new AccountEventType(
  'Create call',
  `
  * Communication > Create new call > *fill out composer* > Submit
  `,
);
export const CreateChargemasterItem = new AccountEventType(
  'Create chargemaster item',
  `
  * Settings > Chargemaster > *any chargemaster* > New code > *fill out modal* > Submit
  `,
);
export const CreateDenialsAdvancedRule =
  new AccountEventType<AdvancedRuleAnalyticProperties>(
    'Create denials advanced rule',
    `
  * Settings > Denials > Assignments > Advanced rules > Create rule > *fill out modal* > Submit
  `,
  );
export const CreateDenialsAutomation =
  new AccountEventType<DenialsAutomationAnalyticalProperties>(
    'Create denials automation',
    `
  * Settings > Denials > Automation > Create automation > *fill out modal* > Submit
  `,
  );
export const CreateDenialsExclusionRule =
  new AccountEventType<ExclusionRuleAnalyticProperties>(
    'Create denials exclusion rule',
    `
  * Settings > Denials > General > Create exclusion > *fill out modal* > Submit
  `,
  );
export const CreateDenialsInstruction =
  new AccountEventType<DenialsInstructionAnalyticProperties>(
    'Create denials instruction',
    `
  * Denials > Worklist > *any claim* > *any line item* > Add instructions > *fill out modal* > Submit
  * Settings > Denials > Instructions > Create instruction > *fill out modal* > Submit
  `,
  );
export const CreateEstimate = new AccountEventType(
  'Create estimate',
  `
  * Estimates > Create estimate > *fill out modal* > Submit
  * Patients > *any patient* > Estimates > Create estimate > *fill out modal* > Submit
  * Eligibility > *eligibility without error* > Create estimate > *fill out modal* > Submit
  * Appointments > *any appointment* ⋯ > New estimate
  * Appointments > *any appointment* ⋯ > New estimate from template > *fill out modal* > Submit
  `,
);
export const CreateEstimateTemplate = new AccountEventType(
  'Create estimate template',
  `
  * Patients > Estimates > *any estimate* > Templates > Save as template > *fill out modal* > Submit
  * Settings > Estimates > Estimate templates > New template > *fill out modal* > Submit
  `,
);
export const CreateFeeSchedule = new AccountEventType(
  'Create fee schedule',
  `
  * Fee Schedules > Create schedule > *fill out modal* > Submit
  * Payers > *any payer* > Fee schedules > Create schedule > *fill out modal* > Submit
  `,
);
export const CreateModelerDraft = new AccountEventType(
  'Create modeler draft',
  `
  * Modeler > *any scenario* > New draft > *complete modal* > Submit
  `,
);
export const CreateModelerNote = new AccountEventType(
  'Create modeler note',
  `
  * Modeler > *any workbook* > Actions > Notes > *type note in* > Save
  `,
);
export const CreateModelerScenario = new AccountEventType(
  'Create modeler scenario',
  `
  * Modeler > Create scenario > *complete modal* > Submit
  `,
);
export const CreateModelerWorkbook = new AccountEventType<{
  'reference type': 'fee schedule' | 'cms baseline';
  'num comparisons': number;
  'num fee schedule comparisons': number;
  'num baseline comparisons': number;
  'utilization type': 'claims' | 'utilization files';
  'num utilization files selected': number;
}>(
  'Create modeler workbook',
  `
  * Modeler > Create new workbook > *follow steps* > Complete
  `,
);
export const CreateNonresponseAssignment =
  new AccountEventType<AdvancedRuleAnalyticProperties>(
    'Create non-response assignment',
    `
  * Settings > Non-response > Assignments > Create assignment > *fill out modal* > Submit
  `,
  );
export const CreateNonresponseAutomation =
  new AccountEventType<NonresponseAutomationAnalyticalProperties>(
    'Create non-response automation',
    `
  * Settings > Non-response > Automations > Create automation > *fill out modal* > Submit
  `,
  );
export const CreateNote = new AccountEventType(
  'Create note',
  `
  * Communication > Create new note > *fill out composer* > Submit
  `,
);
export const CreateNoteTemplate = new AccountEventType(
  'Create note template',
  `
  * Estimates > *any incomplete estimate* > Notes and appearance > *click note template dropdown* > Create note template > *fill out modal* > Submit
  * Settings > Estimates > Note templates > Create note template > *fill out modal* > Submit
  `,
);
export const CreatePayerContact =
  new AccountEventType<PayerContactAnalyticProperties>(
    'Create payer contact',
    `
  * Payers > *any payer* > Contacts > Create contact > *fill out modal* > Submit
  * Denials > Worklist > *any claim* > *any line item* > *create or edit instruction* > Attach contact > Create contact > *fill out modal* > Submit
  * Settings > Denials settings > Instructions > Create instruction > Attach contact > Create contact > *fill out modal* > Submit
  `,
  );
export const CreatePayerFormFill = new AccountEventType(
  'Create payer form fill',
  `
  * Denials > Worklist > *any claim* > Appeal assistance > *click on an appeal form*
  * Denials > Worklist > *any claim* > *any line item* > *any instruction with an appeal form* > *click on an appeal form*
  `,
);
export const CreatePayerFormTemplate =
  new AccountEventType<PayerFormTemplateAnalyticProperties>(
    'Create payer form template',
    `
  * Payers > *any payer* > Forms > Create template > *fill out modal* > Submit
  * Denials > Worklist > *any claim* > *any line item* > *create or edit instruction* > Attach form > Create form > *fill out modal* > Submit
  * Settings > Denials settings > Instructions > Create instruction > Attach form > Create form > *fill out modal* > Submit
  `,
  );
export const CreatePayerPolicy =
  new AccountEventType<PayerPolicyAnalyticProperties>(
    'Create payer policy',
    `
  * Payers > *any payer* > Policies > Create policy > *fill out modal* > Submit
  * Denials > Worklist > *any claim* > *any line item* > *create or edit instruction* > Attach policy > Create policy > *fill out modal* > Submit
  * Settings > Denials settings > Instructions > Create instruction > Attach policy > Create policy > *fill out modal* > Submit
  `,
  );
export const CreateProjectNote = new AccountEventType<{
  isReminder: boolean;
}>(
  'Create project note',
  `
  * Projects > *any project* > *project actions* > *notes and reminders* > *type note in* > *save*
  * Projects > *any project* > *project actions* > *notes and reminders* > *type note in* > *add reminder* > *select date* > *save*
  `,
);
export const CreateProvider = new AccountEventType<{
  'has preferred locations': boolean;
}>(
  'Create provider',
  `
  * Settings > Providers > Create provider > *fill out modal* > Submit
  `,
);
export const DeleteDenialsAutomation = new AccountEventType(
  'Delete denials automation',
  `
  * Settings > Denials > Automation > *any automation* ⋯ > Delete > Confirm
  `,
);
export const DeleteDenialsInstruction = new AccountEventType(
  'Delete denials instruction',
  `
  * Denials > Worklist > *any claim* > *any line item* > *any instruction* > Edit > Delete instruction > Confirm
  * Settings > Denials > Instructions > *any instruction* > Delete > Confirm
  `,
);
export const DeleteDocumentComment = new AccountEventType<{
  isRiveter: boolean;
}>(
  'Delete document comment',
  `
  * Payer documents > *any document row* > three dot menu > click on View Comments > *any comment* > three dot menu > click Delete
  `,
);
export const DeleteEstimate = new AccountEventType<{
  status: SentenceCase<TableEstimationStatus> | null;
}>(
  'Delete estimate',
  `
  * Estimates > *any estimate* ⋯ > Delete > Confirm
  * Patients > *any patient* > *any estimate* ⋯ > Delete > Confirm
  `,
);
export const DeleteNonresponseAutomation = new AccountEventType(
  'Delete non-response automation',
  `
  * Settings > Non-response > Automations > *any automation* ⋯ > Delete > Confirm
  `,
);
export const DeletePayerContact = new AccountEventType(
  'Delete payer contact',
  `
  * Payers > *any payer* > Contacts > *any contact* ⋯ > Delete > Confirm
  `,
);
export const DeletePayerFormFill = new AccountEventType(
  'Delete payer form fill',
  `
  * Denials > Worklist > *any claim* > Appeal assistance > *any form fill* > Delete > Confirm
  `,
);
export const DeletePayerFormTemplate = new AccountEventType(
  'Delete payer form template',
  `
  * Payers > *any payer* > Forms > *any form* ⋯ > Delete > Confirm
  * Payers > *any payer* > Forms > *open any form* > Delete > Confirm
  `,
);
export const DeletePayerPolicy = new AccountEventType(
  'Delete payer policy',
  `
  * Payers > *any payer* > Policies > *any policy* ⋯ > Delete > Confirm
  `,
);
export const DeleteProjectNote = new AccountEventType(
  'Delete project note',
  `
  * Projects > *any project* > *project actions* > *notes and reminders* > *any note - options* > delete
  `,
);
export const DeleteSavedView =
  new AccountEventType<SavedViewAnalyticalProperties>(
    'Delete saved view',
    `
  * *any table that supports saved views* > *any saved view* ⋯ > Delete
  `,
  );
export const DownloadCoproviderRequestForm = new AccountEventType(
  'Download co-provider request form',
  `
  * Estimates > *any completed estimate* > Create co-provider estimate request > *fill out modal* > Submit
  * Patients > *any patient* > Estimates > *any completed estimate* > Create co-provider estimate request > *fill out modal* > Submit
  `,
);
export const DownloadEstimatePdf = new AccountEventType<{
  language: SentenceCase<Language>;
}>(
  'Download estimate PDF',
  `
  * Estimates > *any completed estimate* > Download PDF
  * Patients > *any patient* > Estimates > *any completed estimate* > Download PDF
  `,
);
export const DownloadPayerFormFill = new AccountEventType(
  'Download payer form fill',
  `
  * Denials > Worklist > *any claim* > Appeal assistance > *any form fill* > Download
  `,
);
export const DownloadPayerFormTemplate = new AccountEventType(
  'Download payer form template',
  `
  * Payers > *any payer* > Forms > *any form* > Download
  `,
);
export const DownloadPaymentReceipt = new AccountEventType<{
  language: SentenceCase<Language>;
}>(
  'Download payment receipt',
  `
  * Estimates > *any completed estimate with a payment* > Payment receipt
  * Patients > *any patient* > Estimates > *any completed estimate with a payment* > Payment receipt
  `,
);
export const ManageProject = new AccountEventType<{
  action:
    | 'Delete'
    | 'Rename'
    | 'Download'
    | 'Move to completed'
    | 'Move back to sent'
    | 'Move back to draft';
}>(
  'Manage project',
  `
  * Projects > *any project card options* > Delete project > Yes, delete
  * Projects > *any project card options* > Rename > *rename to anything* > Press enter
  * Project Details > Project actions > Delete project > Yes, delete
  * Project Details > Project actions > Rename > *rename to anything* > Press enter
  * Project Details > Project actions > Download
  * Project Details > Project actions > Move to completed
  * Project Details > Project actions > Move back to active
  `,
);
export const DrillDownToClaimsFromAnalytics = new AccountEventType<{
  'table name': string;
}>(
  'Drill down to claims from analytics',
  `
  * Claims analytics > *click on any row*
  `,
);
export const DrillDownToCategoryInModeler = new AccountEventType<{
  'group by': string;
  'levels deep': number;
  context: 'modal' | 'workbook';
}>(
  'Drill down to category in modeler',
  `
  * Modeler > *any workbook grouped by category* > *click a category link*
  * Modeler > *any workbook grouped by rendering provider* > *click a rendering provider link*
  * Modeler > *any workbook grouped by specialty* > *click a specialty link*
  `,
);
export const DuplicateEstimate = new AccountEventType(
  'Duplicate estimate',
  `
  * Estimates > *any completed estimate* > Copy to new
  * Patients > *any patient* > Estimates > *any completed estimate* > Copy to new
  `,
);
export const EditAutoEstimateRule = new AccountEventType(
  'Edit auto estimate rule',
  `
  * Settings > Estimates > Automation > Location mapping > *any rule* ⋯ > Edit > *fill out modal* > Submit
  `,
);
export const EditBenchmarkSettings = new AccountEventType<{
  'num codes changed': number;
}>(
  `Edit Benchmark settings`,
  `* Benchmark > *open settings modal* > *change 1 or more codes* > *submit form*`,
);
export const EditCall = new AccountEventType(
  'Edit call',
  `
  * Communication > *any call* ⋯ > Edit > *fill out composer* > Submit
  `,
);
export const EditChargemaster = new AccountEventType(
  'Edit chargemaster',
  `
  * Settings > Chargemaster > *any chargemaster* ⋯ > Edit > *fill out modal* > Submit
  `,
);
export const EditChargemasterItem = new AccountEventType(
  'Edit chargemaster item',
  `
  * Settings > Chargemaster > *any chargemaster* > *any item* ⋯ > Edit > *fill out modal* > Submit
  `,
);
export const EditComment = new AccountEventType<NoteAnalyticProperties>(
  'Edit comment',
  `
  * Claims > *any claim* > *any comment you added* ⋯ > Edit > *fill out modal* > Submit
  * Denials > Reminders and activity > *any reminder* ⋯ > Edit > *fill out modal* > Submit
  * Denials > Worklist > *any claim* > Activity > *any comment you added* ⋯ > Edit > *fill out modal* > Submit
  * Non-response > *any claim* > *any comment you added* ⋯ > Edit > *fill out modal* > Submit
  * Underpayments > *any claim* > *any comment you added* ⋯ > Edit > *fill out modal* > Submit
  * Estimates > *any estimate* > Activity timeline > *any comment you added* ⋯ > Edit > *fill out modal* > Submit
  `,
);
export const EditDenialsAdvancedRule =
  new AccountEventType<AdvancedRuleAnalyticProperties>(
    'Edit denials advanced rule',
    `
  * Settings > Denials > Assignments > Advanced rules > *any advanced rule* ⋯ > Edit > *fill out modal* > Submit
  `,
  );
export const EditDenialsAssignments = new AccountEventType<{
  by: SentenceCaseFromCamelCase<AssignmentType>;
  'num assignments': number;
  'num distinct assignees': number;
}>(
  'Edit denial assignments',
  `
  * Settings > Denials Settings > Assignments
  `,
);
export const EditDenialsAutomation =
  new AccountEventType<DenialsAutomationAnalyticalProperties>(
    'Edit denials automation',
    `
  * Settings > Denials > Automations > *any automation* ⋯ > Edit > *fill out modal* > Submit
  `,
  );
export const EditDenialsExclusionRule =
  new AccountEventType<ExclusionRuleAnalyticProperties>(
    'Edit denials exclusion rule',
    `
  * Settings > Denials > General > *any exclusion* ⋯ > Edit > *fill out modal* > Submit
  `,
  );
export const EditDenialsInstruction =
  new AccountEventType<DenialsInstructionAnalyticProperties>(
    'Edit denials instruction',
    `
  * Denials > Worklist > *any claim* > *any line item* > *any instruction* > Edit > *fill out modal* > Submit
  * Settings > Denials > Instructions > *any instruction* ⋯ > Edit > *fill out modal* > Submit
  `,
  );
export const EditDenialStatuses =
  new AccountEventType<IssueStatusesAnalyticProperties>(
    'Edit denial statuses',
    `
  * Settings > Denials > Statuses > Edit statuses > *fill out modal* > Submit
  `,
  );
export const EditDocumentComment = new AccountEventType<{
  isRiveter: boolean;
}>(
  'Edit document comment',
  `
  * Payer documents > *any document row* > three dot menu > click on View Comments > *any comment* > three dot menu > click Edit then Save
  `,
);
export const EditFeeScheduleDetails = new AccountEventType(
  'Edit fee schedule details',
  `
  * Fee schedules > *any fee schedule* ⋯ > Edit > *fill out modal* > Submit
  * Payers > *any payer* > Fee schedules > *any fee schedule* ⋯ > Edit > *fill out modal* > Submit
  * Payers > *any payer* > Fee schedules > *open any fee schedule* > Actions > Edit > *fill out modal* > Submit
  `,
);
export const EditFeeScheduleAdjustments = new AccountEventType(
  'Edit fee schedule adjustments',
  `
  * Fee schedules > *any fee schedule* > Adjustments > *change system rule set*
  * Fee schedules > *any fee schedule* > Adjustments > *any rule or modifier* > Edit > *fill out modal* > Submit
  `,
);
export const EditModelerUtilizationCarveouts = new AccountEventType<{
  'group by': string;
  'num lines edited': number;
}>(
  'Edit modeler utilization carveouts',
  `
  * Modeler > *any workbook* > *triple dot menu for any comparison* > Edit codes or group > *edit carveouts* > Save
  `,
);
export const EditNonresponseAssignment =
  new AccountEventType<AdvancedRuleAnalyticProperties>(
    'Edit non-response assignment',
    `
  * Settings > Non-response > Assignments > *any assignment ⋯ > Edit > *fill out modal* > Submit
  `,
  );
export const EditNonresponseAutomation =
  new AccountEventType<NonresponseAutomationAnalyticalProperties>(
    'Edit non-response automation',
    `
  * Settings > Non-response > Automations > *any automation* ⋯ > Edit > *fill out modal* > Submit
  `,
  );
export const EditNonresponseStatuses =
  new AccountEventType<IssueStatusesAnalyticProperties>(
    'Edit non-response statuses',
    `
  * Settings > Non-response > Statuses > Edit statuses > *fill out modal* > Submit
  `,
  );
export const EditNote = new AccountEventType(
  'Edit note',
  `
  * Communication > *any note* ⋯ > Edit > *fill out composer* > Submit
  `,
);
export const EditNoteTemplate = new AccountEventType(
  'Edit note template',
  `
  * Settings > Estimates > Note templates > *any note template* ⋯ > Edit > *fill out modal* > Submit
  `,
);
export const EditPayerContact =
  new AccountEventType<PayerContactAnalyticProperties>(
    'Edit payer contact',
    `
  * Payers > *any payer* > Contacts > *any contact* ⋯ > Edit > *fill out modal* > Submit
  * Denials > Worklist > *any claim* > *any line item* > *create or edit instruction* > Attach contact > *select contact* > Edit > *fill out modal* > Submit
  * Settings > Denials settings > Instructions > *create or edit instruction* > Attach contact > *select contact* > Edit > *fill out modal* > Submit
  `,
  );
export const EditPayerFormFill = new AccountEventType(
  'Edit payer form fill',
  `
  * Denials > Worklist > *any claim* > Appeal assistance > *any form fill* > *fill out form*
  `,
);
export const EditPayerFormTemplate =
  new AccountEventType<PayerFormTemplateAnalyticProperties>(
    'Edit payer form template',
    `
  * Payers > *any payer* > Forms > *any form* ⋯ > Edit > *fill out modal* > Submit
  * Denials > Worklist > *any claim* > *any line item* > *create or edit instruction* > Attach form > *select form* > Edit > *make changes in editor*
  * Settings > Denials settings > Instructions > *create or edit instruction* > Attach form > *select form* > Edit > *make changes in editor*
  `,
  );
export const EditPayerPolicy =
  new AccountEventType<PayerPolicyAnalyticProperties>(
    'Edit payer policy',
    `
  * Payers > *any payer* > Policies > *any policy* ⋯ > Edit > *fill out modal* > Submit
  * Denials > Worklist > *any claim* > *any line item* > *create or edit instruction* > Attach policy > *select policy* > Edit > *fill out modal* > Submit
  * Settings > Denials settings > Instructions > *create or edit instruction* > Attach policy > *select policy* > Edit > *fill out modal* > Submit
  `,
  );
export const EditSavedViewDetails =
  new AccountEventType<SavedViewAnalyticalProperties>(
    'Edit saved view details',
    `
  * *table that supports saved views* > *any saved view* ⋯ > *change name or shared status*
  `,
  );
export const EditScheduleMappings = new AccountEventType(
  'Edit schedule mappings',
  `
  * Payers > *any payer* > Schedule mapping > *edit a rule*
  `,
);
export const EditUnderpaymentStatuses =
  new AccountEventType<IssueStatusesAnalyticProperties>(
    'Edit underpayment statuses',
    `
  * Settings > Underpayments > Statuses > Edit statuses > *make changes in modal* > Submit
  `,
  );
export const EncounterPatientInsuranceIssue = new AccountEventType<{
  issue: SentenceCaseFromSnakeCase<PatientInsuranceIssue>;
  'num patients with issue': number;
}>(
  'Encounter patient insurance issue',
  `
  * Eligibility > Check eligibility > *fill out modal* > Submit > *encounter one or more eligibility errors*
  `,
);
export const ExcludeIssuesFromProject = new AccountEventType(
  'Exclude issues from a project',
  `
  * Projects > project detail page > *exclude from project*
  `,
);
export const ExcludeLineItemsFromProject = new AccountEventType(
  'Exclude line items from a project',
  `
  * Projects > project detail page > *exclude from project*
  `,
);
export const ExportChart = new AccountEventType<{
  'table name': string;
  'file type': 'PDF' | 'CSV';
}>(
  'Export chart',
  `
  * Revenue diagnostics > *any section with a chart* ⋯ > Export chart as PDF
  `,
);
export const ExportTable = new AccountEventType<{
  'table name': string;
  'with filters'?: boolean;
}>(
  'Export table',
  `
  * Appointments > Export CSV
  * Eligibility > Export CSV
  * Estimates > Export CSV
  * Patients > Export CSV
  * Patients > *any patient* > Estimates > Export CSV
  * Payments > Export CSV
  * All rates > Export CSV
  * Base schedules > *any base schedule* > Export CSV
  * Claims > Export CSV
  * Denials > Worklist > Export CSV
  * Denials > Analytics > View report > Export CSV
  * Non-response > Export CSV
  * Fee schedules > *any fee schedule* > Rates > Export CSV
  * Modeler > *any scenario* > *any draft with a utilization file uploaded* ⋯ > Export CSV
  * Payers > *any payer* > Contacts > Export CSV
  * Pricing optimization > Export chargemaster
  * Underpayments > Export CSV
  * Revenue diagnostics > *any section* ⋯ > Export table as CSV
  * Claims analytics > *any category* > Download
  * Contract terms > Export CSV
  * Renewals and expirations > Export CSV
  * Estimates ROI > Export CSV
  * SuperScore > Export CSV
  * Procedure comparison > *enter a procedure code* > Export CSV
  * Benchmark > *make selections in sidebar* > *click a payer in the table to open the rates modal* > Export CSV
  * Settings > Users > Export CSV
  * Settings > Providers > Export CSV
  * Settings > Chargemaster > *any chargemaster* > Export CSV
  `,
);
export const ExportTransactions = new AccountEventType(
  'Export transactions',
  `
  * Revenue diagnostics > Export
  `,
);
export const ExportWorkbook = new AccountEventType<{
  'export type': 'current view' | 'all data';
  'columns exported': string[];
  'time to download (sec)': number;
}>(
  'Export workbook',
  `
  * Modeler > *any workbook* > Download > Current view
  * Modeler > *any workbook* > Download > All data in workbook
  `,
);
export const GoToSuperSearchResult = new AccountEventType<{
  'result type': string;
  'result name': string | '(PHI)';
  'result details': string | '(PHI)';
}>(
  'Go to super search result',
  `
  * Search > *type a search query* > *any search result*
  `,
);
export const GroupTable = new AccountEventType<{
  'table name': string;
  'group by':
    | SentenceCaseFromCamelCase<Exclude<AppointmentFilters['groupBy'], 'none'>>
    | 'Similar denials'
    | SentenceCaseFromKebabCase<KeyMetricsGrouping>
    | null;
}>(
  'Group table',
  `
  * Appointments > *change grouping setting in sidebar*
  * Denials > Worklist > *toggle group similar denials*
  * Revenue diagnostics > Key metrics > *change group by*
  `,
);
export const ImportBaseSchedule = new AccountEventType<{
  type: 'Procedure' | 'MS DRG';
}>(
  'Import base schedule',
  `
  * Base schedules > Create schedule > *fill out modal* > Submit
  * Base schedules > MS DRG > Create schedule > *fill out modal* > Submit
  `,
);
export const ImportPublishedFeeSchedules = new AccountEventType(
  'Import published fee schedules',
  `
  * Payers > *any payer* > Fee schedules > Import published schedules > *fill out modal* > Submit
  `,
);
export const StopImportingPublishedFeeSchedules = new AccountEventType(
  'Stop importing published fee schedules',
  `
  * Payers > *any payer* > Fee schedules > Stop importing published schedules > Submit
  `,
);
export const ImportChargemaster = new AccountEventType<{
  'merge into existing': boolean;
}>(
  'Import chargemaster',
  `
  * Settings > Chargemaster > Import chargemaster > *upload file* > *map fields* > Submit
  `,
);
export const IncludeIssuesInProject = new AccountEventType(
  'Include issues in a project',
  `
  * Projects > project detail page > *include in project*
  `,
);
export const IncludeLineItemsInProject = new AccountEventType(
  'Include line items in a project',
  `
  * Projects > project detail page > *include in project*
  `,
);
export const ManageProjectNotification = new AccountEventType<{
  type: 'notification' | 'reminder';
  action: 'delete' | 'mark as unread' | 'mark as read';
}>(
  'Manage project notification',
  `
  * Projects > *notification bell* > *any notification - options* > *delete*
  * Projects > *notification bell* > *any notification - options* > *mark as read*
  * Projects > *notification bell* > *any notification - options* > *mark as unread*
  * Projects > *notification bell* > *reminders* > *any reminder - options* > *delete*
  * Projects > *notification bell* > *reminders* > *any reminder - options* > *mark as read*
  * Projects > *notification bell* > *reminders* > *any reminder - options* > *mark as unread*
  `,
);
export const MarkAllProjectsNotificationsAsRead = new AccountEventType<{
  type: 'notifications' | 'reminders';
}>(
  'Mark all projects notifications as read',
  `
  * Projects > *notification bell* > *mark all as read*
  `,
);
export const MarkReminderAsComplete = new AccountEventType<{
  'date created': string;
  'due date': string;
  'date completed': string;
  'is bulk action': boolean;
}>(
  'Mark reminder as complete',
  `
  * Denials > Reminders and activity > *checkmark a reminder*
  `,
);
export const OpenComposer = new AccountEventType<{
  type: 'Email' | 'SMS' | 'Call' | 'Note';
  'is bulk composer': boolean;
}>(
  'Open composer',
  `
  * Communication > Create new email / Create new SMS
  * Communication > Create new call / note
  * Estimates > *checkmark estimates* > Send email / Send SMS
  * Estimates > *any completed estimate* ⋯ > Send email / Send SMS
  * Estimates > *any completed estimate* > Send email / Send SMS
  * Denials > Worklist > *any claim* > Appeal assistance > *any completed form fill* > Send
  * Payers > *any payer* > Forms > *any form* > Send
  * Underpayments > *checkmark claims* > Actions > Email
  `,
);
export const OpenModal = new AccountEventType<{
  'modal title': string;
}>(
  'Open modal',
  `
  * *open any modal*
  `,
);
export const OpenSlideout = new AccountEventType<{
  'slideout title': string;
}>(
  'Open slideout',
  `
  * *open any slideout*
  `,
);
export const OpenSuperSearch = new AccountEventType(
  'Open super search',
  `
  * Search
  * *keyboard shortcut*
  `,
);
export const PreviewEstimatePdf = new AccountEventType<{
  language: SentenceCase<Language>;
}>(
  'Preview estimate PDF',
  `
  * Estimates > *any incomplete estimate* > Preview
  `,
);
export const ProcessPayment = new AccountEventType<
  ProcessPaymentAnalyticProperties & {
    'submitted by': 'Office staff';
  }
>(
  'Process payment',
  `
  * Estimate > *any completed estimate* ⋯ > Process payment > *fill out modal* > Submit
  `,
);
export const RefundPayment = new AccountEventType<{
  amount: string;
  'did succeed': boolean;
  'error message': string;
}>(
  'Refund payment',
  `
  * Payments > *any payment* ... > Void / Refund > Confirm
  `,
);
export const RenameComparison = new AccountEventType<{
  'is reference': boolean;
}>(
  'Rename comparison',
  `
  * Modeler > *any workbook* > *triple dot menu for any comparison* > Rename > *rename comparison*
  `,
);
export const RenameWorkbook = new AccountEventType(
  'Rename workbook',
  `
  * Modeler > *triple dot menu for any workbook* > *rename workbook*
  * Modeler > *any workbook* > Actions > Rename workbook > *rename workbook*
  `,
);
export const ReorderComparisons = new AccountEventType<{
  'num comparisons reordered': number;
}>(
  'Reorder comparisons',
  `
  * Modeler > *any workbook* > Actions > Reorder comparisons > *reorder comparisons* > Save
  `,
);
export const ReplaceEstimateWithTemplate = new AccountEventType(
  'Replace estimate with template',
  `
  * Estimates > *any estimate* > Templates > Use an existing template > *select a template* > Confirm
  `,
);
export const RepostPayment = new AccountEventType<{
  result: SentenceCase<PaymentTransactionPostStatus>;
}>(
  'Repost payment',
  `
  * Payments > *any payment* ⋯ > Retry post
  `,
);
export const ResendReceipt = new AccountEventType(
  'Resend receipt',
  `
  * Estimates > *any estimate with a payment* ⋯ > Resend receipt
  `,
);
export const RunProcedureComparison = new AccountEventType(
  'Run procedure comparison',
  `
  * Procedure comparison > *enter a procedure code*
  `,
);
export const SaveEstimateAsTemplate = new AccountEventType(
  'Save estimate as template',
  `
  * Estimates > *any incomplete estimate* > Templates > Save as template > *fill out modal* > Submit
  * Estimates > *any completed estimate* > Save as template > *fill out modal* > Submit
  `,
);
export const SaveView = new AccountEventType<SavedViewAnalyticalProperties>(
  'Save view',
  `
  * *table that supports saved views* > *apply filter* > Create new view > *fill out modal* > Submit
  `,
);
export const SendClaimStatusCheck = new AccountEventType<{
  'is bulk action': boolean;
  'num items selected': number;
  payer: string | '(Mixed)';
  'days since submission': number | '(Mixed)';
  'has unavailable payer': boolean | 'Unknown';
  'is first request': boolean | '(Mixed)';
}>(
  'Send claim status check',
  `
  * Non-response > *checkmark claims* > Submit claim status check > Confirm
  * Non-response > *any claim with an available payer* > Submit claim status check > Confirm
  `,
);
export const SendEstimate = new AccountEventType<
  ComposerAnalyticProperties & SendEstimateAnalyticProperties
>(
  'Send estimate',
  `
  * Estimates > *checkmark estimates* > Send email / Send SMS > *fill out composer* > Send
  * Estimates > *any completed estimate* ⋯ > Send email / Send SMS > *fill out composer* > Send
  * Estimates > *any completed estimate* > Send email / Send SMS > *fill out composer* > Send
  `,
);
export const SendPayerFormFill = new AccountEventType(
  'Send payer form fill',
  `
  * Denials > Worklist > *any claim* > Appeal assistance > *any completed form fill* > Send > *fill out composer* > Send
  `,
);
export const SendPayerFormTemplate = new AccountEventType(
  'Send payer form template',
  `
  * Denials > Worklist > *any claim* > Appeal assistance > *any completed form fill* > Send > *fill out composer* > Send
  `,
);
export const SendPlainCommunication = new AccountEventType<{
  method: 'Email' | 'SMS';
  'is bulk action': boolean;
}>(
  'Send plain communication',
  `
  * Communication > Create new email / Create new SMS > *fill out composer* > Send
  * Payers > *any payer* > Forms > *any form* > Send > *fill out composer* > Send
  * Underpayments > *checkmark claims* > Actions > Email > *fill out composer* > Send
  `,
);
export const SetAssignees = new AccountEventType<{
  'num assignees selected': number;
  'is bulk action': boolean;
  'num items selected': number;
}>(
  'Set assignees',
  `
  * Estimates > *any estimate* > *open assignees dropdown* > *change assignees* > Apply
  * Estimates > *checkmark estimates* > Assign > Clear assignees or *change assignees* > Apply
  * Patients > *any patient* > Estimates > *checkmark estimates* > Assign > Clear assignees or *change assignees* > Apply
  * Denials > Worklist > *any claim* > *open assignees dropdown* > *change assignees* > Apply
  * Denials > Worklist > *checkmark denials* > Assign > *change assignees* > Apply
  * Non-response > *any claim* > *open assignees dropdown* > *change assignees* > Apply
  * Non-response > *checkmark claims* > Assign > *change assignees* > Apply
  * Underpayments > *any claim* > *open assignees dropdown* > *change assignees* > Apply
  * Underpayments > *scroll rightward to assignees column* > *open any claim's assignees dropdown* > *change assignees* > Apply
  * Underpayments > *checkmark claims* > Assign > *change assignees* > Apply
  * Project Details > *assignment dropdown* > *any user* > Apply
  * Projects > *any project card options* > Assign project > *assign any user* > Apply
  `,
);
export const SetDateRange = new AccountEventType<{
  'length in months': number;
}>(
  'Set forecast date range',
  `
  * Nexus > Reports > *change date range*
  `,
);
export const SetDueDate = new AccountEventType(
  'Set due date',
  `
  * Patients > Estimates > *any estimate* > due date* > *any date*
  * Create Estimate > due date > *any date*
  `,
);
export const SetFeeSchedule = new AccountEventType(
  'Set fee schedule',
  `
  * Patients > Estimates > *any incomplete estimate* > *fee schedule dropdown select*
  `,
);
export const SetForecastDateRange = new AccountEventType<{
  'length in months': number;
}>(
  'Set forecast date range',
  `
  * Revenue diagnostics > *change date range*
  `,
);
export const SetForecastViewBy = new AccountEventType<{
  'view by':
    | 'Date of service'
    | 'Date of payment'
    | 'Bill date'
    | 'Date of remit transaction';
}>(
  'Set forecast view by',
  `
  * Revenue diagnostics > *click an option for view data by*
  * Nexus > Reports> * click any option for view data by*
  `,
);
export const SetForecastScenario = new AccountEventType<{
  metric: 'claim number' | 'gross charges';
  'custom value': number;
  'rivet forecast': number;
  'is actual': boolean;
}>(
  'Set custom forecast scenario',
  `
  * Revenue diagnostics >
  `,
);

export const SetScheduleOverride = new AccountEventType<{
  'from schedule id': number | 'Mixed (bulk action)' | null;
  'to schedule id': number;
  'is bulk action': boolean;
  'num items selected': number;
}>(
  'Set schedule override',
  `
  * Underpayments > *any claim* > *click pencil next to mapped schedule* > *fill out override modal* > Apply
  * Underpayments > *checkmark claims* > Actions > Override mapped fee schedule > *fill out modal* > Apply
  `,
);
export const SetStatusForDeniedClaims = new AccountEventType<{
  'old statuses': (string | null)[];
  'new status': string | null;
  'is bulk action': boolean;
  'num items selected': number;
}>(
  'Set status for denied claim(s)',
  `
  * Denials > Worklist > *any claim* > *click status dropdown* > *click a status*
  * Denials > Worklist > *checkmark claims* > Set status > *click a status*
  `,
);
export const SetStatusForNonresponseClaims = new AccountEventType<{
  'old statuses': (string | null)[];
  'new status': string | null;
  'is bulk action': boolean;
  'num items selected': number;
}>(
  'Set status for non-response claim(s)',
  `
  * Non-response > *any claim* > *click status dropdown* > *click a status*
  * Non-response > *checkmark claims* > Set status > *click a status*
  `,
);
export const SetStatusForPayments = new AccountEventType<{
  'old status': 'Posted' | 'Not posted' | 'Error';
  'new status': 'Posted' | 'Not posted';
  'is bulk action': boolean;
  'num items selected': number;
}>(
  'Set status for payment(s)',
  `
  * Payments > *any payment* > *change posted status in posted column*
  `,
);
export const SetStatusForUnderpaidClaims = new AccountEventType<{
  'old statuses': (string | null)[];
  'new status': string | null;
  'is bulk action': boolean;
  'num items selected': number;
}>(
  'Set status for underpaid claim(s)',
  `
  * Underpayments > *any claim* > *click status dropdown* > *click a status*
  * Underpayments > *scroll rightward to status column* > *open any claim's status dropdown* > *click a status*
  * Underpayments > *checkmark claims* > Actions > Set status > *click a status*
  `,
);
export const SetStatusForUnderpaidLineItems = new AccountEventType<{
  'old statuses': (string | null)[];
  'new status': string | null;
  'is bulk action': boolean;
  'num items selected': number;
}>(
  'Set status for underpaid line item(s)',
  `
  * Underpayments > *any claim* > *any line item* > *click payment issue dropdown* > *click a status*
  `,
);
export const SetModelerGroupBy = new AccountEventType<{
  'group by': string;
}>(
  'Set modeler group by',
  `
  * Modeler > *any workbook* > *click any option for group by*
  `,
);
export const SetModelerViewBy = new AccountEventType<{
  'view by': string;
}>(
  'Set modeler view by',
  `
  * Modeler > *click any option for view by*
  `,
);
export const SetViewBy = new AccountEventType<{
  'view by': 'Date of service' | 'Date of remit transaction';
}>(
  'Set forecast view by',
  `
  * Nexus > Reports> * click any option for view data by*
  `,
);
export const SkipClaimOrClaimGroup = new AccountEventType<{
  direction: 'Forward' | 'Backward';
  'single or group': 'Single' | 'Group';
}>(
  'Skip claim or claim group',
  `
  * Denials > Worklist > *any claim* > Previous group / Previous claim / Next group / Next claim
  `,
);
export const SortTable = new AccountEventType<{
  'table name': string | '(Unknown)';
  'sort by': string | null;
  direction: 'Ascending' | 'Descending' | null;
}>(
  'Sort table',
  `
  * Revenue diagnostics > Key metrics > *change sort by*
  * *any table* > *click on a sortable header to change the sort*
  * Projects > *click on order by dropdown* > *select any option*
  * Modeler > *any workbook* > *click on a sortable header to change the sort*
  `,
);

export const ApplyBreakdown = new AccountEventType<{
  'breakdown name': string;
  'num breakdowns': number;
  'metric names': string[];
  action: 'add' | 'edit';
}>(
  'Apply breakdown',
  `
  * Revenue diagnostics > Reports > *any chart* > *any breakdown*
  `,
);

export const RemoveBreakdown = new AccountEventType<{
  'breakdown names': string[];
  'num breakdowns': number[];
  'metric names': string[];
  source: 'trash icon' | 'clear all';
}>(
  'Remove breakdown',
  `
  * Revenue diagnostics > Reports > *any chart with breakdown* > *any breakdown* > *trash icon*
  * Revenue diagnostics > Reports > *any chart with breakdown* > clear all
  `,
);

export const SubmitManualPayment = new AccountEventType<{
  amount: number;
}>(
  'Submit manual payment',
  `
  * Estimates > *any estimate* ⋯ > Payments > *fill out modal* > Record payment
  * Patients > *any patient* > Estimates > *any estimate* ⋯ > Payments > *fill out modal* > Record payment
  `,
);
export const UndoRedoModeler = new AccountEventType<{
  operation: 'undo' | 'redo';
  changed: 'utilization' | 'schedule rule';
}>(
  'Undo or redo in Modeler',
  `
  * Modeler > *any workbook* > *update utilization source* > Undo last utilization change
  * Modeler > *any workbook* > *undo utilization source change* > Redo last utilization change
  * Modeler > *any workbook* > *update schedule rule* > Undo last rule change
  * Modeler > *any workbook* > *undo schedule rule change* > Redo last rule change
  `,
);
export const UpdateSavedView =
  new AccountEventType<SavedViewAnalyticalProperties>(
    'Update saved view',
    `
  * *table that supports saved views* > *click any saved view* > *change a filter* > Update
  `,
  );
export const UpdateUtilizationSourceFromWorkbookSlideout =
  new AccountEventType<{
    'previous utilization source type': 'claims' | 'utilization files' | null;
    'new utilization source type': 'claims' | 'utilization files';
  }>(
    'Update utilization source from workbook slideout',
    `
  * Modeler > *any workbook* > Actions > Update utilization source > *update source* > Update
  `,
  );
export const ViewAllOfGroupedTable = new AccountEventType<{
  'group name': string;
  'num rows in group': number;
}>(
  'View all of grouped table',
  `
  * Appointments > *select a group by* > *any group with over 10 appointments* > View all
  * Denials > Worklist > *enable group similar* > *any group with over 10 claims* > View all
  * Claims analytics > *any category with over 10 items* > View all
  `,
);
export const ViewBenchmarkRates = new AccountEventType<{
  source: 'bar chart' | 'table';
  'billing code': string | null;
  'comparison level': string | null;
}>(
  `View benchmark rates`,
  `* Benchmark > *click one of the bars in the chart or click the payer name in the table*`,
);
export const ViewEstimatePdf = new AccountEventType<{
  language: SentenceCase<Language>;
}>(
  'View estimate PDF',
  `
  * Estimates > *any completed estimate* > View
  `,
);
export const ViewMoreFromDenialsProductivityMetricCard = new AccountEventType<{
  'card title': string;
}>(
  'View more from denials productivity metric card',
  `
  * Denials > *any metric card* > View
  `,
);
export const ViewNexusBreakdown = new AccountEventType<{
  'active filters': string[];
}>(
  'View breakdowns in Nexus',
  `
  * Nexus > Breakdown > *any filters*
  `,
);
export const ViewNexusClaim = new AccountEventType<{
  'active filters': string[];
}>(
  'View claims in Nexus',
  `
  * Nexus > Claims > *any claim*
  `,
);
export const ViewNexusReports = new AccountEventType<{
  'report title': string;
}>(
  'View reports in Nexus',
  `
  * Nexus > Reports > *any report*
  `,
);
export const ViewPayerContactCard = new AccountEventType(
  'View payer contact card',
  `
  * Denials > Worklist > *any claim* > *any line item* > *any instruction with a contact* > *click on a contact to see their card*
  `,
);
export const ViewPayerPolicy = new AccountEventType<{
  'policy type': 'Link' | 'File';
}>(
  'View payer policy',
  `
  * Denials > Worklist > *any claim* > *any line item* > *any instruction with a policy* > *click on a policy*
  * Payers > *any payer* > Policies > *any policy*
  `,
);
export const ViewProjectsNotificationsTab = new AccountEventType(
  'Toggle to the projects notifications tab',
  `
  * Projects > *notification bell* > *notifications*
  `,
);
export const ViewProjectsRemindersTab = new AccountEventType(
  'Toggle to the projects reminders tab',
  `
  * Projects > *notification bell* > *reminders*
  `,
);
export const ViewTransactionHistory = new AccountEventType<{
  type: 'Chronological' | 'Itemized';
}>(
  'View transaction history',
  `
  * Claims > *any claim* > View claim
  * Denials > Worklist > *any claim* > Transaction history
  * Underpayments > *any claim* > Transaction history
  `,
);

// ------------------------------------------------------------
// Payer Portal Automator (Blackbeard)

/**
 * Helper function to make sure PPA event names and types match
 */
function ppaEvent<T extends keyof PPA_AnalyticsEvents>(
  name: T,
  description: string,
) {
  return new AccountEventType<PPA_AnalyticsEvents[T]>(name, description);
}

export const AutofillInputWithPPA = ppaEvent(
  'Autofill input with PPA',
  `
  * **url**: the base url of the payer portal site
  * **label**: the input element label that the PPA used to suggest autofill options
  * **search**: the search query the user typed in to filter results
  * **selected**: the display name of the autofill option that was actually selected
  `,
);

export const OpenRivvyChatbot = new AccountEventType<{}>(
  'Open Rivvy Chatbot',
  `
  * Prevent > Ask Rivet AI
  `,
);
